import React from 'react';
import { Box, Select, MenuItem, TextField, InputAdornment, FormHelperText } from '@mui/material';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';

// Country dial-code list (mirrors the login form's phone widget).
export const COUNTRIES = [
  { code: 'IN', label: 'India', dial: '+91', flag: '🇮🇳' },
  { code: 'US', label: 'United States', dial: '+1', flag: '🇺🇸' },
  { code: 'GB', label: 'United Kingdom', dial: '+44', flag: '🇬🇧' },
  { code: 'AU', label: 'Australia', dial: '+61', flag: '🇦🇺' },
  { code: 'CA', label: 'Canada', dial: '+1', flag: '🇨🇦' },
  { code: 'AE', label: 'UAE', dial: '+971', flag: '🇦🇪' },
  { code: 'SG', label: 'Singapore', dial: '+65', flag: '🇸🇬' },
];

// sqip is Canada-first; US/CA share +1 so we bias to Canada unless there's a strong signal.
export function detectDefaultCountry() {
  const canada = COUNTRIES.find((c) => c.code === 'CA') || COUNTRIES[0];
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const CA_TZ = ['Toronto', 'Vancouver', 'Edmonton', 'Winnipeg', 'Halifax', 'Montreal',
      'Regina', 'St_Johns', 'Moncton', 'Whitehorse', 'Yellowknife', 'Iqaluit'];
    if (CA_TZ.some((z) => tz.includes(z))) return canada;
    const locales = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || ''];
    for (const loc of locales) {
      const region = (loc.split('-')[1] || '').toUpperCase();
      if (region && region !== 'US') {
        const match = COUNTRIES.find((c) => c.code === region);
        if (match) return match;
      }
    }
  } catch (e) { /* ignore */ }
  return canada;
}

export const defaultCountry = detectDefaultCountry();

// Combine a selected country + local number into E.164 (or '' when empty).
export function composeE164(country, phone) {
  const raw = String(phone || '').trim();
  if (!raw) return '';
  // If they already typed a full international number, keep it (don't double the code).
  if (raw.startsWith('+')) return `+${raw.replace(/\D/g, '')}`;
  return `${(country && country.dial) || '+1'}${raw.replace(/\D/g, '')}`;
}

// Reusable "country code + phone number" field. Parent holds `country` and `phone` state
// and calls composeE164(country, phone) at submit time.
export default function PhoneField({ country, setCountry, phone, setPhone, label = 'Phone number',
  error = false, helperText = '', onBlur }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'stretch' }}>
        <Select
          value={country.code}
          onChange={(e) => setCountry(COUNTRIES.find((c) => c.code === e.target.value) || defaultCountry)}
          size="small"
          sx={{ minWidth: 104, borderRadius: 2.5, bgcolor: 'background.paper', fontWeight: 500, flexShrink: 0, '& .MuiSelect-select': { py: '8px', px: 1 } }}
          renderValue={(v) => {
            const c = COUNTRIES.find((x) => x.code === v);
            return c ? <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{c.flag} <span style={{ fontSize: '0.9rem' }}>{c.dial}</span></span> : v;
          }}
        >
          {COUNTRIES.map((c) => <MenuItem key={c.code} value={c.code}>{c.flag} {c.label} ({c.dial})</MenuItem>)}
        </Select>
        <TextField
          fullWidth size="small" label={label} placeholder="9876543210" type="tel"
          value={phone} onChange={(e) => setPhone(e.target.value)} onBlur={onBlur} error={error}
          InputProps={{ startAdornment: (<InputAdornment position="start"><PhoneOutlinedIcon fontSize="small" color="action" /></InputAdornment>) }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: 'background.paper' } }}
        />
      </Box>
      {helperText && <FormHelperText error={error} sx={{ ml: 1 }}>{helperText}</FormHelperText>}
    </Box>
  );
}
